import { tokens } from '@ping/design-tokens';
import { colors, spacing, typography, motion } from '../foundations';
import type { ColorScheme } from '../foundations';

export interface Theme {
  colors: {
    surface: {
      primary: string;
      secondary: string;
      tertiary: string;
      app: string;
      panel: string;
      elevated: string;
      pressed: string;
    };
    text: {
      primary: string;
      secondary: string;
      tertiary: string;
      muted: string;
      disabled: string;
      onAccent: string;
    };
    action: {
      primary: string;
    };
    accent: {
      indigo: string;
      rose: string;
      mint: string;
      amber: string;
      violet: string;
      teal: string;
      pink: string;
      green: string;
      coral: string;
      slate: string;
      orchid: string;
      sky: string;
      brand: string;
      verified: string;
    };
    border: {
      primary: string;
      secondary: string;
      subtle: string;
      strong: string;
    };
    icon: {
      default: string;
      muted: string;
      onAccent: string;
    };
    presence: {
      online: string;
    };
    overlay: {
      scrim: string;
    };
  };
  spacing: typeof spacing;
  typography: typeof typography;
  motion: typeof motion;
  radii: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
    xl: number;
    pill: number;
  };
  shadows: {
    sm: {
      shadowColor: string;
      shadowOffset: { width: number; height: number };
      shadowOpacity: number;
      shadowRadius: number;
      elevation: number;
    };
    md: {
      shadowColor: string;
      shadowOffset: { width: number; height: number };
      shadowOpacity: number;
      shadowRadius: number;
      elevation: number;
    };
    lg: {
      shadowColor: string;
      shadowOffset: { width: number; height: number };
      shadowOpacity: number;
      shadowRadius: number;
      elevation: number;
    };
  };
}

export function createTheme(colorScheme: ColorScheme): Theme {
  const colorSchemeTokens = colors[colorScheme];

  return {
    colors: colorSchemeTokens,
    spacing,
    typography,
    motion,
    radii: tokens.radius,
    shadows: {
      sm: {
        shadowColor: tokens.shadow.sm.color,
        shadowOffset: { 
          width: tokens.shadow.sm.offsetX, 
          height: tokens.shadow.sm.offsetY 
        },
        shadowOpacity: 0.1,
        shadowRadius: tokens.shadow.sm.blur / 2,
        elevation: 1,
      },
      md: {
        shadowColor: tokens.shadow.md.color,
        shadowOffset: { 
          width: tokens.shadow.md.offsetX, 
          height: tokens.shadow.md.offsetY 
        },
        shadowOpacity: 0.15,
        shadowRadius: tokens.shadow.md.blur / 2,
        elevation: 3,
      },
      lg: {
        shadowColor: tokens.shadow.lg.color,
        shadowOffset: { 
          width: tokens.shadow.lg.offsetX, 
          height: tokens.shadow.lg.offsetY 
        },
        shadowOpacity: 0.2,
        shadowRadius: tokens.shadow.lg.blur / 2,
        elevation: 5,
      },
    },
  };
}

export const lightTheme = createTheme('light');
export const darkTheme = createTheme('dark');
